import { FC, useCallback, useMemo } from 'react';
import { Prohibited } from '~/components/parcel-details/prohibited/prohibited';
import { BaseModuleProps } from './module-factory';
import { BasketSections } from '~/lib/types/enums';
import useBasketSection from '~/hooks/use-basket-section';
import { BasketQuote, Legal, QuoteExtras } from '~/lib/types/basket-types';
import Section from '~/components/core/section/section';
import { useProtection } from '~/hooks/use-protection';
import { Protection } from '~/lib/types/extra-types';
import useQuoteRequirements from '~/hooks/use-quote-requirements';
import { getTermsLinks } from '~/lib/quotes';

const AcceptTermsModule: FC<BaseModuleProps> = ({ errors, wrapper }) => {
  const { requiresCustoms } = useQuoteRequirements();
  const legal = useBasketSection<Legal>({ name: BasketSections.LEGAL });
  const extras = useBasketSection<QuoteExtras>({ name: BasketSections.EXTRAS });
  const quote = useBasketSection<BasketQuote>({ name: BasketSections.QUOTE });
  const { protection, currency } = useProtection();

  const serviceTerms = getTermsLinks(quote?.data);
  const serviceTermsChecked = serviceTerms
    ? (legal.data?.serviceTermsAccepted?.[serviceTerms.courier] ?? false)
    : false;
  const onServiceTermsChange = useCallback(
    (checked: boolean) => {
      if (serviceTerms) {
        legal.update({
          ...legal.data,
          serviceTermsAccepted: {
            ...legal.data.serviceTermsAccepted,
            [serviceTerms.courier]: checked
          }
        });
      }
    },
    [serviceTerms, legal]
  );

  const extraExtra = useMemo(
    () => protection.options.find((x) => x.key === Protection.FULL && extras.data?.extras.some((e) => e.key === x.key)),
    [protection, extras.data]
  );

  const hasTax = useMemo(
    () =>
      quote.data?.availableExtras?.some(
        (x) => (x.key === Protection.FULL || x.key === Protection.BASE) && x.price?.tax > 0
      ),
    [quote]
  );

  return wrapper(
    <Section dataTestId="terms-section" loading={false}>
      <Prohibited
        protectionPrice={extraExtra?.price?.net || 0}
        currency={currency}
        errors={errors}
        hasTax={hasTax}
        requiresCustoms={requiresCustoms}
        checked={legal.data.termsAccepted || false}
        onChange={(checked) => legal.update({ ...legal.data, termsAccepted: checked })}
        serviceTerms={serviceTerms}
        serviceTermsChecked={serviceTermsChecked}
        onServiceTermsChange={onServiceTermsChange}
      />
    </Section>
  );
};

export default AcceptTermsModule;
